import type { ClientTags, ClientTagKey } from "./client_tags";
import { Timestamp } from "firebase/firestore";

import { User } from "firebase/auth";
export type EmailToSanitize = Pick<
    IOutboundEmail | INewOutboundEmail | IEmailReminder,
    "cc" | "recipient"
>;
export type TOffices = { [key: string]: string[] };
export type TLocationAddress = { [key: string]: string };
export type TOfficeManager = { [key: string]: string[] };
export type TRequestedService = { [key: string]: string };
export type DepartmentServices = {
    [department: string]: { [key: string]: string | string[] };
};

export enum ClientInquiryMethod {
    PSYCH_TODAY = "Psych Today",
    ZDOC = "Zdoc",
    WEB_FORM = "Webform",
    PARKHILL_WEBFORM = "Parkhill Webform",
    PARKHILL_WEBFORM_INCOMPLETE = "Parkhill Webform Incomplete",
    WEB_FORM_INCOMPLETE = "Webform Incomplete",
    GOOD_THERAPY = "Good Therapy",
    PHONE = "Phone",
    EMAIL = "Email",
    INTERNAL = "Internal",
    MOVE = "Move",
    INSURANCE_CHECK = "Insurance Check",
    THERAPY_DEN = "Therapy Den",
    MENTAL_HEALTH_MATCH = "Mental Health Match",
    THERAPY_FOR_LATINX = "Therapy for Latinx",
    CHOOSING_THERAPY = "Choosing Therapy",
    SUBWAYS_ADS = "Subways Ads",
    WELL_TRACK = "Welltrack",
    THERAPY_FOR_BLACK_GIRLS = "Therapy For Black Girls",
    MI_RESOURCE = "MiResource",
}

export enum RequestedServices {
    CHILD_THERAPY = "Child Therapy",
    COUPLES_THERAPY = "Couples Therapy",
    FAMILY_THERAPY = "Family Therapy",
    GIFTED_ASSESSMENT = "Gifted Assessment",
    SHORT_TERM_EVALUATIONS_DOCUMENTATION_SERVICES = "Short Term Evaluations & Documentation Services",
    INDIVIDUAL_THERAPY = "Individual Therapy",
    MEDICATION_MANAGEMENT = "Medication Management",
    PSYCHOLOGICAL_ASSESSMENT = "Psychological Assessment",
    NUTRITION_COUNSELING = "Nutrition Counseling",
    TELETHERAPY = "Teletherapy",
}

export enum AvailabilityStatus {
    AVAILABLE = "available",
    UNAVAILABLE = "unavailable",
}

export enum BookingStatus {
    HELD = "Held",
    CONFIRMED = "Confirmed",
}

export enum ClinicianMethodology {
    CM = "Conventional Medicine",
    AH = "Alternative or holistic",
    BTB = "By the book",
    CBT = "CBT",
    PCHC = "Psychodynamic",
    SO = "Science Oriented",
    FUNNY = "Funny",
    DIRECT = "Direct",
    SP = "Strong Personality",
    WARM = "Warm",
    RELIGION = "Religion",
    CP = "Cerebal Palsy",
    SJ = "Social Justice",
    EXISTENTIAL = "Existential",
    SE = "Self-Esteem",
    DS = "Down Syndrome",
    HSPE = "HSP or Empath",
    DE = "Disordered Eating",
}

export enum ClinicianCredential {
    EXTERN = "Extern",
    INTERN = "Intern",
    LMSW = "LMSW",
    LCSW = "LCSW",
    LCSW_A = "LCSW-A",
    LCSW_B = "LCSW-B",
    LCSW_C = "LCSW-C",
    LMHC = "LMHC",
    LMHC_A = "LMHC-A",
    LMHC_B = "LMHC-B",
    LMHC_C = "LMHC-C",
    MHCLP = "MHC-LP",
    PHD = "PhD",
    PhD_A = "PhD-A",
    PhD_B = "PhD-B",
    PhD_C = "PhD-C",
    PNP = "PNP",
    PNP_A = "PNP-A",
    PNP_B = "PNP-B",
    PNP_C = "PNP-C",
    PSYD = "PsyD",
    PsyD_A = "PsyD-A",
    PsyD_B = "PsyD-B",
    PsyD_C = "PsyD-C",
    RDN = "RDN",
}
export enum AccessedBy {
    INTAKE_TEAM = "Intake Team",
    CLINICIAN = "Clinician",
}
export enum ReferralMethods {
    CANDY = "Candy",
    OPEN_PATH = "Open path",
    Ironworkers = "Ironworkers",
    Van_Cleef = "Van Cleef",
    Academics_West = "Academics West",
    Charlie_Health = "Charlie Health",
    ERC_Pathlight = "ERC Pathlight",
    Nutrition_by_JEM = "Nutrition by JEM",
    Well_Behavioral_Health = "Well Behavioral Health",
    Sanctuary = "Sanctuary",
    Willow_Health = "Willow Health",
    Yonkers_Fire_Department = "Yonkers Fire Department",
    Withum = "Withum",
    Department_of_Sanitation = "Department of Sanitation",
    Boram_Care = "Boram Care",
    Beyond_Med = "Beyond Med",
    Eclaro = "Eclaro",
    NYC_Bar_Association = "NYC Bar Association",
    NYFA = "NYFA",
    WMHC = "WMHC",
    RENFREW = "Renfrew",
    REVEL = "Revel",
    UpHealth = "UpHealth",
    Subway_Ads = "Subway Ads",
    SOCIAL_MEDIA = "Social Media",
    THERAPY_FOR_LATINX = "Therapy for Latinx",
    UPHEALTH = "Uphealth",
    ACADIA = "Acadia",
    BRIDGES_MENTAL_HEALTH = "Bridges Mental Health",
    CHOOSING_THERAPY = "Choosing Therapy",
    CWA = "CWA",
    FREED_MAXICK = "Freed Maxick",
    MENTAL_HEALTH_MATCH = "Mental Health Match",
    NQTTCN = "NQTTCN",
    Jane_Street = "Jane Street",
    OTHER = "Other",
}
export enum RecurrenceRule {
    WEEKLY = "Weekly",
    BIWEEKLY = "Bi-Weekly",
    TRIWEEKLY = "Tri-Weekly",
    MONTHLY = "Monthly",
    ONE_OFF = "One-Off",
    TWO_MONTHS = "Two-Months",
    THREE_MONTHS = "Three-Months",
}

export enum ClinicianSearchCredential {
    EXTERN = "Extern",
    INTERN = "Intern",
    LMSW = "LMSW",
    LCSW = "LCSW",
    LMHC = "LMHC",
    MHCLP = "MHC-LP",
    PHD = "PhD",
    PNP = "PNP",
    PSYD = "PsyD",
    RDN = "RDN",
}

export enum AgeCategory {
    TWENTIES = "20s",
    THIRTIES = "30s",
    FOURTIES = "40s",
    FIFTIES = "50s",
    OVER_FIFTIES = "Over 50s",
}

export enum TimeCategory {
    EARLY_MORNING = "Early Morning",
    MORNING = "Morning",
    AFTERNOON = "Afternoon",
    EVENING = "Evening",
    LUNCH_TIME = "Lunch Time",
}
export enum UserSex {
    MALE = "M",
    FEMALE = "F",
    UNKNOWN = "U",
}

export const TimeCategoryHours = {
    [TimeCategory.EARLY_MORNING]: [6, 7, 8],
    [TimeCategory.MORNING]: [9, 10, 11],
    [TimeCategory.LUNCH_TIME]: [12],
    [TimeCategory.AFTERNOON]: [13, 14, 15],
    [TimeCategory.EVENING]: [16, 17, 18, 19, 20],
};

export enum DayOption {
    SUNDAY = "Sunday",
    MONDAY = "Monday",
    TUESDAY = "Tuesday",
    WEDNESDAY = "Wednesday",
    THURSDAY = "Thursday",
    FRIDAY = "Friday",
    SATURDAY = "Saturday",
}

export const DayOptionDays = {
    [DayOption.SUNDAY]: [0],
    [DayOption.MONDAY]: [1],
    [DayOption.TUESDAY]: [2],
    [DayOption.WEDNESDAY]: [3],
    [DayOption.THURSDAY]: [4],
    [DayOption.FRIDAY]: [5],
    [DayOption.SATURDAY]: [6],
};

interface Window {
    id: string;
    start: string;
    end: string;
}

export interface IOfficeWindow extends Window {
    office: string;
    location: string;
    clinicianId?: string;
    blocked?: boolean;
    ending_date?: string;
}
export interface SupervisorPair {
    practice_id: string;
    supervisor_id: string;
    supervisor_first_name?: string;
    supervisor_last_name?: string;
}

export interface OfficeMangerOfficeWindow extends Window {
    office: string;
    location: string;
    clinicianId?: string;
    blocked?: boolean;
    ending_date?: string;
    start_date?: string;
    end_date?: string;
    blocked_reason?: string;
}
export interface IReservedSchedule extends IAvailabilitySchedule {
    reservedFor?: string;
    clinicianName?: string;
    clinicianId?: string;
    serviceDeliveryOption?: ServiceDeliveryOption;
    practice_id?: string;
}

export type DateType = string | Date;

export interface ISchedule {
    id?: string;
    calendarId?: string;
    title?: string;
    body?: string;
    start?: DateType;
    end?: DateType;
    goingDuration?: number;
    comingDuration?: number;
    isAllDay?: boolean;
    category?: string;
    dueDateClass?: string;
    location?: string;
    attendees?: string[];
    clinician_id?: string;
    client_id?: string;
    recurrenceRule?: RecurrenceRule;
    isPending?: boolean;
    isFocused?: boolean;
    isVisible?: boolean;
    isReadOnly?: boolean;
    isPrivate?: boolean;
    color?: string;
    bgColor?: string;
    dragBgColor?: string;
    borderColor?: string;
    customStyle?: string;
    raw?: {
        [propName: string]: string | number | boolean | object | null;
    };
    state?: string;
}

export enum DeleteActions {
    CLINICIAN_DELETING_OFFICE_WINDOW = "clinician_deleting_office_window",
    CLINICIAN_DELETING_AVAILABILITY = "clinician_deleting_availability",
    ADMIN_DELETING_CLINICIAN_OFFICE_WINDOW = "admin_deleting_clinician_office_window",
}
export interface IAvailabilitySchedule extends ISchedule {
    office?: string;
    canEdit?: boolean;
    starting_date?: string;
    ending_date?: string;
    availableService?: string;
    serviceDeliveryOption?: ServiceDeliveryOption;
    action?: DeleteActions;
    updated_at?: string;
    status?: string;
}

export interface AvailableClinicians {
    clinician_name: string;
    no_of_available_slots: number;
    availabilities: IAvailabilityWindow[];
    is_priority: boolean;
}

export interface HeldOfficeProps {
    clinicianId: string;
    start: string;
    end: string;
    location: string;
    office: string;
}

export type IOfficeSchedule = IAvailabilitySchedule;

export interface IAvailabilityWindow extends Window {
    recurrenceRule?: RecurrenceRule;
    location?: string;
    office?: string;
    reservedFor?: string;
    last_synced_to_zocdoc?: Date | null;
    ending_date?: string;
    availableService?: string;
    starting_date?: string;
}

export type ServiceDeliveryOption = "in_person" | "remote" | undefined;

export interface IIntakeAvailability {
    id: string;
    start_time: string;
    end_time: string; // always 45 mins
    recurrence: RecurrenceRule.ONE_OFF;
    service_delivery_option: ServiceDeliveryOption;
    location: string;
    office: string;
    status: "held" | "confirmed" | "available";
    created_at: Date;
    booking_id?: string;
    client_id?: string;
    client_display_name?: string;
    clinician_display_name: string;
    supervisees_display_name?: string;
    supervisor_id: string;
    practice_id?: string;
    completed_at?: Date | null;
}

export interface IIntakeSessionHistory {
    id: string;
    booking_id: string;
    supervisor_id: string;
    practice_id: string;
    start_time: string;
    end_time: string;
    status: "held" | "confirmed" | "available";
    actioned_by: string;
    action: "cancelled" | "rescheduled";
    actioned_on: Date;
    reason?: string;
    recurrence?: RecurrenceRule;
    service_delivery_option?: ServiceDeliveryOption;
    location?: string;
    clinician_display_name: string;
    booking_requested_service?: string;
    booking_clinician_display_name?: string;
    booking_start?: string;
    booking_starting_date?: string;
    booking_practice_id?: string;
}

export interface IClientBooking extends IAvailabilityWindow {
    clinician_display_name: string;
    client_display_name: string;
    clinician_id: string;
    client_id: string;
    status: BookingStatus;
    clinician_contacted_client?: boolean;
    starting_date: string;
    requested_service: string;
    created_by_clinician?: boolean;
    is_paused?: boolean;
    date_paused?: string;
    location?: string;
    office?: string;
    practice_id: string;
    service_delivery_option?: ServiceDeliveryOption;
    confirmed_on?: string | null;
    held_on?: string | null;
    department?: string;
    email_notes?: string;
    supervisor_intake_session?: IIntakeAvailability | null;
    session_attendees?: ISessionAttendee[];
}

export enum LowHighExperienceLevel {
    LOW = "Low",
    MEDIUM = "Medium",
    HIGH = "High",
    NONE = "None",
}

export enum YesNoExperienceLevel {
    YES = "yes",
    NO = "no",
}

export enum ConvFluentExpLevel {
    CONVERSATIONAL = "Conversational",
    FLUENT = "Fluent",
}

export interface IExperienceLevel {
    [key: string]: {
        level?: LowHighExperienceLevel | YesNoExperienceLevel | ConvFluentExpLevel;
        do_not_want?: boolean;
        certified?: YesNoExperienceLevel;
        credential_expiration_date?: string;
    };
}

/**
 *
 * - CRONJOB: Update on client/clincian doc from daily automated cron job
 * - CALENDAR: Update on client/clincian doc from Google Calendar sync
 * - MANTLE: Update on client/clincian doc from Mantle app
 * - INTAKE_BOOKING_FORM: Update on client/clincian doc from Public intake booking form
 * - INTAKE: Update on client/clincian doc from Internal intake system
 * - SYSTEM: Automatic system-triggered updates
 */

export enum UpdatedFrom {
    CLIENT_TAGS = "Client_Tags",
    CRONJOB = "Cronjob",
    CALENDAR = "Calendar",
    MANTLE = "Mantle",
    INTAKE_BOOKING_FORM = "Intake_Booking_Form",
    INTAKE = "Intake",
    SYSTEM = "System",
}

export interface ClientRepairDiagnosticsMetadata {
    restored_at?: Date | Timestamp;
    restored_by?: string;
    source?: ClientRepairDiagnosticsSource;
    last_repaired_at?: Date | Timestamp;
    last_repaired_by?: string;
    action_ids?: string[];
}

export interface IClinician {
    id?: string;
    email: string;
    fullname: string;
    firstname: string;
    lastname: string;
    npi?: string;
    credential: ClinicianCredential;
    contact_info_client: string;
    contact_info_personal: string;
    personality_notes: string;
    dob?: Date;
    start_date?: Date;
    locations: string[];
    methodology: string[];
    experience_levels: IExperienceLevel;
    office_windows: IOfficeWindow[];
    available: IAvailabilityWindow[];
    unavailable: IAvailabilityWindow[];
    held: IClientBooking[];
    confirmed: IClientBooking[];
    supervisor_id: string;
    updated_at: Date;
    is_supervisor: boolean;
    started_practicing?: Date;
    booking_paused?: boolean;
    booking_pause_reason?: string;
    booking_paused_by?: string;
    booking_paused_at?: Date;
    marked_as_priority?: boolean;
    do_not_show_on_website: boolean;
    do_not_sync_to_zocdoc?: boolean;
    departments: string[];
    supervisorFirstName?: string;
    supervisorLastName?: string;
    secondary_supervisor_ids?: SupervisorPair[];
    updated_from?: UpdatedFrom;
    ideal_caseload?: number | null;
    last_synced_to_zocdoc?: Date | null;
    clinician_calendar_id?: string;
    available_for_intake_sessions?: IIntakeAvailability[];
    client_repair_diagnostics?: ClientRepairDiagnosticsMetadata;
}

export type ClinicianStatusChangeField = "booking_paused" | "marked_as_priority";

export interface IClinicianStatusChange {
    id?: string;
    clinician_id: string;
    field: ClinicianStatusChangeField;
    enabled: boolean;
    note: string;
    changed_by: string;
    changed_at: Date;
}

export enum ExperienceLevelType {
    LOWHIGH = "low_to_high",
    YESNO = "yes_or_no",
    CONVFLUENT = "conv_or_fluent",
}

export enum InsuranceYesOrNo {
    YES = "Y",
    NO = "N",
}

export enum RelationshipToPrimaryInsured {
    SELF = "self",
    SPOUSE = "spouse",
    CHILD = "child",
    OTHER = "other",
}

export interface IExperienceLevelOption {
    category: string;
    value: string;
    type: ExperienceLevelType;
    show: boolean;
    credential: string;
    clinician_hidden: boolean;
    certification_toogle: boolean;
}

export interface ISearchParams {
    available_days_only: boolean;
    first_name: string;
    age_categories: AgeCategory[];
    methodology: { [key: string]: boolean }; // ClinicianMethodology
    experience_level: { [key: string]: boolean };
    search_locations: string[];
    search_credentials: string[];
    experience_level_filter: { [key: string]: string };
    show_only_available: boolean;
    available_services: string[];
    priority_marked: boolean;
    search_departments?: string[];
}

export enum ClientStatus {
    INCOMPLETE_INQUIRY = "Incomplete Inquiry",
    INQUIRY = "Initial Inquiry",
    INS_VERIFICATION = "Insurance Verification",
    INS_FOLLOWUP = "Insurance Follow-up",
    FEE_SETTING = "Fee Setting",
    APPT_SETTING = "Appointment Setting",
    FOLLOWUP = "Followup",
    WAITLIST = "Waitlist",
    APPT_HELD = "Appointment Held",
    APPT_CONFIRMED = "Appointment Confirmed",
    COMPLETED = "Completed",
    DO_NOT_BOOK = "Do Not Book",
    NON_RESPONSIVE = "Non-Responsive",
}

export enum InsuranceType {
    MEDICARE = "medicare",
    MEDICAID = "medicaid",
    TRICARE = "tricare",
    CHAMPVA = "champva",
    GROUP_HEALTH_PLAN = "group_health_plan",
    FECA_BLK_LUNG = "feca_blk_lung",
    Other = "other",
}

export interface IInsuranceVerificationFields {
    INNDed: string;
    INNCo: string;
    INNOOP: string;
    INNCoins: string;
    OONDed: string;
    OONCo: string;
    OONOOP: string;
    OONCoins: string;
    UCR: string;
    CalYr: string;
    policy_effective_date: string;
    effective_date: string;
    accumulations_cal_yr: string;
    PlanCalYr: string;
    Accumulations: string;
    insurance_accepted?: string;
    insurance_status?: string;
    last_verification_date?: Date;
    ins_request_notes?: string;
}

export interface IFollowUpFields {
    start_date?: string | null;
    end_date?: string | null;
    ending_date?: string | null;
    followup_notes?: string;
    status_after_end_date?: string;
}

export interface IFeeSettingFields {
    masters_fee: string;
    dr_fee: string;
    dr_fee_a: string;
    dr_fee_b: string;
    dr_fee_c: string;
    psychiatry_fee: string;
    psychiatry_fee_a: string;
    psychiatry_fee_b: string;
    psychiatry_fee_c: string;
    intern_fee: string;
    lic_fee: string;
    kap_fee: string;
    nutrition_fee: string;
    inn_nutrition_fee: string;
    lic_a_fee: string;
    lic_b_fee: string;
    lic_c_fee: string;
    extern_fee: string;
    inn_masters_fee: string;
    career_fee: string;
    gifted_assessment_fee?: string;
    short_term_evaluation_documentation_fee?: string;
    dr_fee_calc?: {};
    dr_fee_a_calc?: {};
    dr_fee_b_calc?: {};
    dr_fee_c_calc?: {};
    masters_fee_calc?: {};
    psychiatry_fee_calc?: {};
    psychiatry_fee_a_calc?: {};
    psychiatry_fee_b_calc?: {};
    psychiatry_fee_c_calc?: {};
    lic_fee_calc?: {};
    kap_fee_calc?: {};
    nutrition_fee_calc?: {};
    inn_nutrition_fee_calc?: {};
    inn_masters_fee_calc?: {};
    lic_a_fee_calc?: {};
    lic_b_fee_calc?: {};
    lic_c_fee_calc?: {};
    intern_fee_calc?: {};
    extern_fee_calc?: {};
    career_fee_calc?: {};
    gifted_assessment_fee_calc?: {};
    short_term_evaluation_documentation_fee_calc?: {};
    is_self_pay?: boolean;
    testing_fee?: string;
    intern_extern_testing_fee?: string;
}

export interface IFeeSelect {
    masters_fee?: boolean;
    dr_fee?: boolean;
    psych_fee?: boolean;
    intern_fee?: boolean;
    testing_fee?: boolean;
    intern_extern_testing_fee?: boolean;
    extern_fee?: boolean;
    career_fee?: boolean;
    gifted_assessment_fee?: boolean;
    short_term_evaluation_documentation_fee?: boolean;
    inn_masters_fee?: boolean;
}

export interface InsuranceCard {
    id: string;
    name: string;
    url: string;
}

export interface IInsuranceFollowUpFields {
    notes_from_ins_team: string;
    notes_from_intake_team: string;
    email_sent: boolean;
    photo_links: InsuranceCard[];
}

export interface IAppointmentSettingFields {
    call_notes?: string;
    email_notes?: string;
    follow_ups?: string;
}

export interface IInquiryFields {
    called_on?: Date;
    emailed_on?: Date;
}

export interface IIncompleteInquiryFields {
    notes_for_incomplete_inquiry?: string;
}
export interface IAppointmentHeldFields {
    cancellation_notes?: string;
    consent_cc_notes?: string;
}

export interface ISessionAttendeeConsentCheck {
    good_form_on_file: boolean;
    rechecked_at?: Date | null;
    rechecked_by?: string | null;
    collateral_consent_form_id?: string | null;
    collateral_consent_form_name?: string | null;
    collateral_consent_form_variant_id?: string | null;
    collateral_consent_form_variant_name?: string | null;
    signed_at?: Date | null;
    sent_at?: Date | null;
    reminder_sent_at?: Date | null;
    reminder_count?: number;
    mantle_source_revision?: string;
}

export interface ISessionAttendee {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    date_of_birth?: string | null;
    relationship?: string;
    guardian_role?: "primary" | "other" | null;
    active: boolean;
    consent_check: ISessionAttendeeConsentCheck;
    created_at: Date;
    updated_at: Date;
}

export type PortalSupportedFormType =
    | "humantold_consent_form"
    | "chp_extern_therapy_consent_form"
    | "chp_testing_consent_form"
    | "chp_psych_appt_consent_form"
    | "chp_nutrition_counseling_consent_form"
    | "chp_career_assessment_consent_form"
    | "kap_consent"
    | "ph_in_network_consent_form"
    | "ph_nutrition_counseling_consent_form";

export interface IAppointmentChecks {
    [appointment_id: string]: {
        date_added: Date;
        consent_form_sent?: Date | null;
        consent_form_reminder_sent?: Date | null;
        consent_form_reminder_count?: number;
        consent_form?: ConsentForm;
        consent_form_type?: PortalSupportedFormType;
        consent_form_rechecked?: Date | null;
        client_submitted?: boolean;
        client_submitted_at?: Date | null;
        consent_error_cc?: Date | null;
        consent_issue_cancel?: Date | null;
        client_added_to_amd?: Date | null;
        credit_card_validated?: Date | null;
        client_requested_call: boolean;
        consult_call_completed?: boolean;
        consult_call_completed_on?: string;
        consult_call_notes: string;
        consent_cc_notes?: string;
        vaccination_checks_completed_on?: string | null;
        supervisor_intake_not_required?: boolean;
        supervisor_intake_not_required_reason?: string;
        collateral_consent_good_form_on_file?: boolean;
        collateral_consent_rechecked?: Date | null;
        collateral_consent_missing_attendee_count?: number;
        mantle_consent_status?: string;
        mantle_consent_source_revision?: string;
        correction_reason?: string | null;
    };
}

export interface ISessionAttendeesHistory {
    booking_id: string;
    saved_at: Date;
    saved_by: string;
    booking_status: "held" | "confirmed";
    booking_requested_service?: string;
    attendees: ISessionAttendee[];
}

export interface IAppointmentHistory extends IClientBooking {
    cancelled_on: string;
    cancellation_notes: string;
    cancelled_by?: string;
    consent_cc_notes?: string;
}

export interface IUpdateFeesDetails {
    updated_by: string;
    updated_at: Date | null;
    reason_for_fee_update?: string;
}

export type WebformStep = 1 | 2 | 3 | 4;

export interface IClient {
    id: string;
    email: string;
    phone_no: string;
    first_name: string;
    primary_parent?: string;
    primary_parent_last_name?: string;
    primary_parent_email?: string;
    primary_parent_phone?: string;
    other_parent?: string;
    other_parent_last_name?: string;
    other_parent_email?: string;
    other_parent_phone?: string;
    has_no_other_parent_guardian?: boolean;
    no_other_parent_guardian_reason?: string;
    preferred_name: string;
    last_name: string;
    pronouns: string;
    address: string;
    state?: string;
    sex?: UserSex | null;
    accept_assignment?: InsuranceYesOrNo;
    city?: string;
    date_of_birth: Date | null;
    zip_code: string;
    insurance_provider: string;
    insurance_member_id: string;
    insurance_plan_employer: string;
    group_number: string;
    insurance_type?: InsuranceType;
    notes: string;
    requested_time: string;
    requested_day: string | string[];
    client_services_notes: string;
    complete_notes: {
        reason: string;
        notes?: string;
        termination_message_requested?: boolean;
        reason_for_termination_msg_skip?: string;
    };
    waitlist_clinician_ids: string[];
    waitlist_locations: string[];
    waitlist_time_categories: TimeCategory[];
    waitlist_days: DayOption[];
    waitlist_wants_intern: boolean;
    waitlist_notes?: string;
    status: ClientStatus;
    requested_location: string[];
    treatment_type: string;
    preferred_therapist_gender: string[];
    presenting_concerns: string[];
    consent_to_email: string;
    preferred_language_of_communication: string[];
    primary_insured_name?: string;
    primary_insured_dob?: Date;
    primary_insured_address?: string;
    primary_insured_state?: string;
    primary_insured_city?: string;
    primary_insured_zipcode?: string;
    relationship_to_primary_insured?: RelationshipToPrimaryInsured;
    clinician_fees?: { [clinicianEmail: string]: IFeeSettingFields };
    [ClientStatus.INCOMPLETE_INQUIRY]?: IIncompleteInquiryFields;
    [ClientStatus.INQUIRY]?: IInquiryFields;
    [ClientStatus.INS_VERIFICATION]?: IInsuranceVerificationFields;
    [ClientStatus.FEE_SETTING]?: IFeeSettingFields;
    [ClientStatus.INS_FOLLOWUP]?: IInsuranceFollowUpFields;
    [ClientStatus.FOLLOWUP]?: IFollowUpFields;
    [ClientStatus.APPT_SETTING]?: IAppointmentSettingFields;
    [ClientStatus.APPT_HELD]?: IAppointmentHeldFields;
    appointment_checks?: IAppointmentChecks;
    appointment_history: IAppointmentHistory[];
    intake_session_history: IIntakeSessionHistory[];
    session_attendees_history?: ISessionAttendeesHistory[];
    held_bookings: IClientBooking[];
    confirmed_bookings: IClientBooking[];
    mantle_session_source_revisions?: Record<string, string>;
    mantle_supervisor_intake_source_revisions?: Record<string, string>;
    inquiry_method: ClientInquiryMethod;
    status_history: { [key: string]: typeof Timestamp | Date };
    has_history?: boolean;
    date_updated?: Date;
    is_current_chart?: boolean;
    old_ids?: string[];
    cancellation_notes?: {
        [key: string]: {
            booking: IClientBooking;
            reason: string;
            notes?: string;
            termination_message_requested?: boolean;
            reason_for_termination_msg_skip?: string;
        };
    };
    requested_service: string;
    requested_therapist: string | string[];
    assigned_to?: string;
    secondary_assignee?: string;
    on_waitlist: boolean;
    fees_updated_by?: string;
    fees_update_details?: IUpdateFeesDetails;
    loaded_fee_revision?: string;
    is_blacklisted: boolean;
    referred_by: string;
    last_updated_by?: string;
    applicable_fee: IFeeSelect;
    hear_about_us?: string;
    suggested_call_date_time?: string;
    consent_to_sms?: string;
    webform_selected_cta_option?: string;
    webform_selected_cta_details?: string;
    client_is_b2b: boolean;
    client_is_on_trial: boolean;
    tracking_params?: string;
    cookie_params?: string;
    inquiry_id: string;
    webform_status: string;
    webform_step?: WebformStep;
    mode_of_communication: string;
    updated_from?: UpdatedFrom;
    source: "Humantold" | "Parkhill Webform";
    client_repair_diagnostics?: ClientRepairDiagnosticsMetadata;
    payment_card_count?: number;
    mantle_payment_card_source_revision?: string;
    mantle_client_profile_source_revisions?: Record<string, string>;
    tags?: ClientTags;
    active_tag_keys?: ClientTagKey[];
    mantle_fee_source_revision?: string;
}

export interface BookingDetails {
    submitted: string;
    last_name: string;
    first_name: string;
    preferred_name: string;
    pronouns: string;
    phone: string;
    email: string;
    insurance_provider: string;
    insurance_member_id: string;
    dob: string;
    address: string;
    state: string;
    zip_code: string;
    pri_ins_name: string;
    pri_ins_address: string;
    pri_ins_dob: string;
    therapist_requested: string | string[];
    service: string;
    referred_by: string;
    comments: string;
    method: string;
    how_did_you_hear_about_us: string;
    suggested_call_date_time: string;
    consent_to_sms: string;
    city: string;
    tracking_params: string;
    cookie_params: string;
    webform_status: ClientStatus;
    webform_step?: WebformStep | `${WebformStep}`;
    webform_selected_cta_option: string;
    webform_selected_cta_details: string;
    requested_day: string | string[];
    requested_time: string;
    source: "Humantold" | "Parkhill Webform";
    photo_links: {
        id: string;
        name: string;
        url: string;
    }[];
    inquiry_id: string;
    mode_of_communication: string;
    treatment_type: string;
    preferred_therapist_gender: string[];
    presenting_concerns: string[];
    consent_to_email: string;
    preferred_language_of_communication: string[];
    location: string[];
}

export enum DateFormats {
    DAY = "dddd",
    DATE_FORMAT = "MMM Do YYYY",
    DAY_WEEK_FORMAT = "ddd, MMMM Do YYYY",
    DAY_MONTH_FORMAT = "MM/DD",
    DAY_MONTH_YEAR_FORMAT = "MM/DD/YYYY",
    DAY_HOUR_FORMAT = "ddd ha",
    DAY_HOUR_MIN_FORMAT = "MM/DD hh:mma",
    DAY_TIME_FORMAT = "ddd hh:mma",
    DAY_YEAR_HOUR_MIN_FORMAT = "MM/DD/YYYY hh:mma",
    DAY_YEAR_HOUR_MIN_SEC_FORMAT = "MM/DD/YYYY hh:mm:ssa",
    HOUR_FORMAT = "ha",
    HOUR_MIN_FORMAT = "h:mma",
}
export interface IGDriveFile {
    description: string;
    embedUrl: string;
    iconUrl: string;
    id: string;
    lastEditedUtc: number;
    mimeType: string;
    name: string;
    parentId: string;
    rotation: number;
    rotationDegree: number;
    serviceId: string;
    sizeBytes: number;
    type: string;
    url: string;
}

export interface ICalculateClientFee {
    customer_status: string;
    insurance: string;
    deductible: string;
    deductible_met: string;
    inn_coinsurance: string;
    inn_copay: string;
    oon_coinsurance: string;
    oon_copay: string;
    ucr: string;
}

export interface ISubmitCalculateClientFee {
    year: string;
    fee_params: ICalculateClientFee[];
}

export interface IFeesCalculated extends ICalculateFeeResponse {
    client_id: string;
}
export interface ICalculateFeeResponse {
    data: {
        masters: string;
        doctoral: string;
    }[];
}

export interface IACL {
    billing?: boolean;
    insurance_verification?: boolean;
    intake?: boolean;
    super_admin?: boolean;
    blocked?: boolean;
    copay?: boolean;
    office_manager?: boolean;
    view_applicable_fees?: boolean;
    can_be_auto_assigned?: boolean;
    care_coordinator?: boolean;
}

export interface IAuthUser {
    user: User | null;
    deleted?: boolean;
    acl?: IACL;
    clinician?: IClinician;
}

export interface IACLEmail {
    email: string;
    assignment?: { weekly_hours?: 35 | 40 | 45 };
    acl?: IACL;
    assignment_color?: string;
    deleted?: boolean;
    deleted_at?: DateType;
    deleted_by?: string;
}

export interface IArchivedClient extends IClient {
    archived_on?: Date;
}

export interface SortConfigProps {
    key: string;
    direction: "asc" | "desc";
}

export interface IReport {
    age: { [key: string]: number };
    completion_reason: { [key: string]: string };
    insurance_provider: { [key: string]: string };
    method: { [key: string]: ClientInquiryMethod };
    status: { [key: string]: ClientStatus };
    therapist_requested: { [key: string]: string | string[] };
    zip_code: { [key: string]: string };
}

export enum OfficeRequestStatus {
    APPROVED = "Approved",
    REJECTED = "Rejected",
    PENDING = "Pending",
    APPROVED_WITH_CHANGES = "ApprovedWithChanges",
    HELD = "Held",
    ON_WAITLIST = "OnWaitlist",
}

export enum OfficeManagerActions {
    APPROVED_REQUEST = "ApprovedRequest",
    REJECTED_REQUEST = "RejectedRequest",
    APPROVED_WITH_CHANGES = "ApprovedRequestWithChanges",
    DELETED_OFFICE_WINDOW = "DeletedOfficeWindow",
    CREATED_OFFICE_WINDOW = "CreatedOfficeWindow",
    HELD_WINDOW = "HeldWIndow",
    HELD_WINDOW_WITH_CHANGES = "HeldWindowWithChanges",
    APPROVED_OFFICE_WINDOW_DELETE_REQUEST = "ApprovedOfficeDeleteRequest",
    REJECTED_OFFICE_WINDOW_DELETE_REQUEST = "RejectedOfficeDeleteRequest",
}

export type ReportDuration = "day" | "week" | "month" | "quarter" | "year";
export const NO_BILLING_COPY = ["SELF-PAY"];
export const NO_SUPERVISOR_COPY = [
    // Aetna, Meritain, 1199 and self pay
    "AETNA",
    "MERITAIN",
    "1199",
    "SELF-PAY",
];

export enum AppSortOrder {
    ASC = "asc",
    DESC = "desc",
}

export const ALL_INSURANCE_PROVIDERS = [
    "1199",
    "AETNA",
    "ALLIED",
    "BCBS - Federal",
    "BCBS",
    "BEACON",
    "CARELON",
    "CIGNA",
    "CORESOURCE",
    "EMBLEM HEALTH",
    "GEHA",
    "GHI",
    "GHI/EMBLEM HEALTH",
    "HARVARD PILGRIM",
    "HUMANA",
    "LUMINARE - 35182",
    "LUMINARE - 62308",
    "MAGNACARE",
    "MERITAIN",
    "METRO",
    "NIPPON LIFE BENEFITS",
    "NYCE PPO PLAN",
    "NYSHIP",
    "OLYMPUS",
    "OPTUM",
    "Other",
    "OXFORD",
    "SELF-PAY",
    "TRUSTMARK",
    "UMR",
    "UNITED STUDENT RESOURCES",
    "UNITED HEALTHCARE SHARED SERVICES",
    "UNITED HEALTHCARE",
    "WELLFLEET",
];

export type MasterListStatus = "active" | "inactive" | "ALL" | undefined;

export enum OutboundEmailStatus {
    UNSCHEDULED = "unscheduled",
    PENDING = "pending",
    SENT = "sent",
    FAILED = "failed",
}

export enum OutboundEmailType {
    APPT_HELD_CLINICIAN_CONSULT = "appt_held_clinician_consult",
    APPT_CONFIRMED_CLINICIAN = "appt_confirmed_clinician",
    RETURNING_CLINICIAN = "returning_clinician",
    CLIENT_ASSIGNED = "client_assigned",
    VERIFICATION_TEAM_NOTIFY = "verification_team_notification",
    REQUESTED_OFFICE_WINDOW = "requested_office_window",
    OFFICE_WINDOW_REQUEST_ACTION = "office_window_request_action",
    OFFICE_WINDOW_DELETE_REQUEST_ACTION = "office_window_delete_request_action",
    DELETED_OFFICE_WINDOWS = "deleted_office_windows",
    CLIENT_TERMINATION_EMAIL = "client_termination_email",
    CREATED_FROM_SEND_GRID = "created_from_send_grid",
    CLINICIAN_RESERVED_TIME_WINDOW = "clincian_reserved_time_window",
    HUMANTOLD_CONSENT_FORM = "humantold_consent_form",
    CHP_EXTERN_THERAPY_CONSENT_FORM = "chp_extern_therapy_consent_form",
    CHP_TESTING_CONSENT_FORM = "chp_testing_consent_form",
    KAP_CONSENT = "kap_consent",
    CHP_CAREER_ASSESSMENT_CONSENT_FORM = "chp_career_assessment_consent_form",
    CHP_NUTRITION_COUNSELING_CONSENT_FORM = "chp_nutrition_counseling_consent_form",
    CHP_PSYCH_APPT_CONSENT_FORM = "chp_psych_appt_consent_form",
    PH_IN_NETWORK_CONSENT_FORM = "ph_in_network_consent_form",
    PH_NUTRITION_COUNSELING_CONSENT_FORM = "ph_nutrition_counseling_consent_form",
    CONSENT_ERROR_CC_AUTH_ONLY = "consent_error_cc_auth_only",
    CONSENT_ISSUE_CANCEL = "consent_issue_cancel",
    CLIENT_CHART_UNLOCKED = "client_chart_unlocked",
    APPT_CONFIRMED_CLIENT_HT_TELETHERAPY = "appt_confirmed_client_ht_teletherapy",
    APPT_CONFIRMED_CLIENT_PH_TELETHERAPY = "appt_confirmed_client_ph_teletherapy",
    APPT_CONFIRMED_CLIENT_CHP_TELETHERAPY = "appt_confirmed_client_chp_teletherapy",
    APPT_CONFIRMED_CLIENT_HT_IN_PERSON = "appt_confirmed_client_ht_in_person",
    APPT_CONFIRMED_CLIENT_PH_IN_PERSON = "appt_confirmed_client_ph_in_person",
    APPT_CONFIRMED_CLIENT_CHP_IN_PERSON = "appt_confirmed_client_chp_in_person",
    APPT_CONFIRMED_CLIENT_CHP_EXTERN_THERAPY = "appt_confirmed_client_chp_extern_therapy",
    APPT_CONFIRMED_CLIENT_CHP_TESTING = "appt_confirmed_client_chp_testing",
    APPT_CONFIRMED_CLIENT_CHP_KETAMINE = "appt_confirmed_client_chp_ketamine",
    APPT_CONFIRMED_CLIENT_CHP_CAREER_ASSESSMENT = "appt_confirmed_client_chp_career_assessment",
    APPT_CONFIRMED_CLIENT_CHP_NUTRITION_COUNSELING = "appt_confirmed_client_chp_nutrition_counseling",
    APPT_CONFIRMED_CLIENT_CHP_PNP_THERAPY = "appt_confirmed_client_chp_pnp_therapy",
    APPT_CONFIRMED_CLIENT_SUPERVISOR_INTAKE = "appt_confirmed_client_supervisor_intake",
    APPT_CONFIRMED_CLINICIAN_SUPERVISOR_INTAKE = "appt_confirmed_clinician_supervisor_intake",
    CLINICIAN_REQUESTED_SERVICE_TYPE_CHANGE = "clinician_requested_service_type_change",
    COLLATERAL_CONSENT_FORM = "collateral_consent_form",
    CLINICIAN_EXPERIENCE_LEVEL_UPDATE = "clinician_experience_level_update",
}

export enum OutboundEmailTypeDescription {
    APPT_HELD_CLINICIAN_CONSULT = "Appointment held emails to Clinicians",
    APPT_CONFIRMED_CLINICIAN = "Appointment confirmed emails to Clinicians",
    RETURNING_CLINICIAN = "Returning clients to clinician emails",
    VERIFICATION_TEAM_NOTIFY = "Verification queue email to Verification team",
    CLIENT_ASSIGNED = "Client assigned emails to Assignees",
    CREATED_FROM_SEND_GRID = "Created from send grid",
    HUMANTOLD_CONSENT_FORM = "Humantold Consent Form emails",
    CHP_EXTERN_THERAPY_CONSENT_FORM = "CHP consent form emails",
    CHP_TESTING_CONSENT_FORM = "CHP Testing Consent Form emails",
    KAP_CONSENT = "KAP Consent Form emails",
    CHP_CAREER_ASSESSMENT_CONSENT_FORM = "CHP Career Assessment Consent Form emails",
    CHP_NUTRITION_COUNSELING_CONSENT_FORM = "CHP Nutrition Counseling Consent Form emails",
    CHP_PSYCH_APPT_CONSENT_FORM = "CHP Psych Appointment Consent Form emails",
    PH_IN_NETWORK_CONSENT_FORM = "PH In Network Consent Form emails",
    PH_NUTRITION_COUNSELING_CONSENT_FORM = "PH Nutrition Counseling Consent Form emails",
    CONSENT_ERROR_CC_AUTH_ONLY = "Consent Error CC Authorization Only",
    CONSENT_ISSUE_CANCEL = "Consent Issue Cancel",
    APPT_CONFIRMED_CLIENT_HT_TELETHERAPY = "HUMANTOLD Confirmation Client (Tele)",
    APPT_CONFIRMED_CLIENT_HT_IN_PERSON = "HUMANTOLD Client Confirmation (In-Person)",
    APPT_CONFIRMED_CLIENT_CHP_EXTERN_THERAPY = "CHP Extern Therapy Client Confirmation (Tele)",
    APPT_CONFIRMED_CLIENT_CHP_TESTING = "CHP Testing Client Confirmation",
    APPT_CONFIRMED_CLIENT_CHP_KETAMINE = "CHP Ketamine Client Confirmation",
    APPT_CONFIRMED_CLIENT_CHP_CAREER_ASSESSMENT = "CHP Career Assessment Client Confirmation",
    APPT_CONFIRMED_CLIENT_CHP_NUTRITION_COUNSELING = "CHP Nutrition Client Confirmation",
    APPT_CONFIRMED_CLIENT_CHP_PNP_THERAPY = "CHP PNP Client Confirmation (Tele)",
    APPT_CONFIRMED_CLIENT_PH_TELETHERAPY = "PARK HILL Confirmation Client (Tele)",
    APPT_CONFIRMED_CLIENT_PH_IN_PERSON = "PARK HILL Client Confirmation (In-Person)",
    APPT_CONFIRMED_CLIENT_CHP_TELETHERAPY = "CHP Client Confirmation (Tele)",
    APPT_CONFIRMED_CLIENT_CHP_IN_PERSON = "CHP Client Confirmation (In-Person)",
    COLLATERAL_CONSENT_FORM = "Collateral Consent Form emails",
    CLINICIAN_EXPERIENCE_LEVEL_UPDATE = "Experience level update notification to clinicians",
}

export interface EmailRecipient {
    email: string;
    first_name: string;
    last_name?: string;
}

export type EmailRecipientType =
    | "clinician"
    | "patient"
    | "internal"
    | "office_manager";

export interface IOutboundEmail {
    id: string;
    recipient_type: EmailRecipientType;
    type: OutboundEmailType;
    subject: string;
    preview_text: string;
    sender: EmailRecipient;
    recipient: EmailRecipient;
    signature?: SignatureData;
    cc?: EmailRecipient[];
    created_at: Date;
    updated_at: Date;
    status: OutboundEmailStatus;
    retries: number;
    last_error?: {};
}

export interface IOutboundSms {
    id?: string;
    from?: string;
    to?: string;
    text?: string;
    created_at?: Date;
    updated_at?: Date;
    status?: string;
    sender?: { email: string; first_name: string };
    retries?: number;
    last_error?: {};
    ringcentral_generated_message_id?: string;
}

export type Overwrite<T1, T2> = {
    [P in Exclude<keyof T1, keyof T2>]: T1[P];
} & T2;

export interface IEmailAppointmentDetails {
    id: string;
    day: string;
    date_str: string;
    time_str: string;
    fee_description?: string;
    location: string;
    street: string;
    suite: string;
    department?: string;
    location_address?: string;
    requested_service: string;
    practice_id: string;
    recurrence_rule: string;
    new_requested_location?: string;
    new_requested_service?: string;
    supervisor_name: string;
    intake_session_practice_id: string;
    supervisor_intake_day: string;
    supervisor_intake_date_str: string;
    supervisor_intake_time_str: string;
}

export type OfficeRequestCategory =
    | "request_list"
    | "wait_list"
    | "delete"
    | "delete_office_window_list";

export interface IEmailRequestDetails {
    date_str: string;
    day: DayOption;
    start_time: string;
    end_time: string;
    location: string;
    office: string;
    category?:
        | "requested an office window at"
        | "joined the wailist for"
        | "sent a request to delete office window";
    clinician_name?: string;
    action?: string;
    reason?: string;
}

export interface IEmailReservedWindowDetails {
    from: string;
    to: string;
    starting_date: string;
    recurrence: RecurrenceRule;
}
export interface ClinicianOfficeWindowRequests {
    location: string;
    office: string;
    start_date: Date;
    end_date: Date;
    ending_date?: Date | string;
    recurrence: RecurrenceRule;
    requester: string;
    request_date: Date;
    change_end_time_reason: string;
    day: string;
    delivery_option: string;
    status?: OfficeRequestStatus;
    rejection_reason?: string;
    office_window_request_id: string;
    client_name?: string;
    assigned_to?: string;
    available?: boolean;
    clinician_name: string;
    category: OfficeRequestCategory;
    request_list_count: number;
    waitlist_count: number;
    approve_with_changes_reason?: string;
    reason_for_holding?: string;
    reason_for_delete?: string;
    comment?: string;
}

export interface IEmailClient {
    preferred_name: string;
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    dob: string | null;
    insurance: string;
    insurance_id: string;
    client_concerns: string;
    additional_notes?: string;
    pronouns: string;
    clinician_display_name?: string;
    clinician_first_name?: string;
    supervisor_name?: string;
    age?: number | null;
    isMinor?: boolean;
    primary_parent_guardian_name?: string;
    other_parent_guardian_name?: string;
    other_parent_guardian_email?: string;
    other_parent_guardian_phone?: string;
    has_no_other_parent_guardian?: boolean;
    no_other_parent_guardian_reason?: string;
    consent_form_link_adult?: string;
    consent_form_link_minor?: string;
    consent_form_link_minor_secondary_custodian?: string;
    single_consent_form_link?: string;
    patient_portal_url?: string;
    consent_portal_link_enabled?: boolean;
    consent_share_link_enabled?: boolean;
    valid_credential?: boolean;
    is_pnp?: boolean;
    requested_service?: string;
    location_address?: string;
    office?: string;
    is_self_paid?: boolean;
    is_rechecked?: boolean;
    supervisor_intake_not_required?: boolean;
    supervisor_intake_not_required_reason?: string;
    supervisor_intake_status?: "not-required" | "required-not-booked" | "booked";
    partner_attendees?: Array<{
        first_name: string;
        last_name: string;
        email?: string;
        relationship?: string;
    }>;
}
export interface IOfficeWindowSchedule extends ISchedule {
    clinicianId?: string;
    office?: string;
    clinician_name?: string;
    assigned_to?: string;
    ending_date?: DateType;
}
export interface TimesUsed {
    start_time: string;
    end_time: string;
}

export interface ICancelClientSessionParams {
    client: IClient;
    clinicians: IClinician[];
    selectedAppointmentId: string;
    cancellerId: string;
    caller: "admin" | "clinician";
    sessionFields: {
        termination_message_requested: boolean;
        reason_for_termination_msg_skip: string;
    };
}

export interface IUpdatedClinicianChanges {
    confirmed: IClinician["confirmed"];
}

export interface IUpdatedClientChanges {
    status: IClient["status"];
    appointment_history: IClient["appointment_history"];
    cancellation_notes: IClient["cancellation_notes"];
    confirmed_bookings: IClient["confirmed_bookings"];
    intake_session_history?: IIntakeSessionHistory[];
}
export interface ISessionCancellationChanges {
    canCancel: boolean;
    updatedClientChangesToSave?: IUpdatedClientChanges;
    updatedClininicianChangesToSave?: {
        [clinician_id: string]: IUpdatedClinicianChanges;
    };
    supervisorIntakeSessionsToCancel?: Array<{
        supervisorId: string;
        bookingId: string;
        clientId: string;
    }>;
    message?: string;
    numberOfCancellations?: number;
}

export interface IEmailDeleteDetails {
    day: DayOption;
    start_time: string;
    end_time: string;
    location: string;
    office: string;
    clinician_name?: string;
    deletedBy?: string;
    reason?: string;
}

export interface SignatureData {
    id: string;
    name: string;
    pronouns: string;
    company: string;
    department: string;
    phone: string;
    directPhone: string;
    email: string;
    includeDisclaimer: boolean;
}

export interface OfficeRequestDetailsProps {
    clinician_name: string;
    start_date: Date;
    end_date: Date;
    location: string;
    office: string;
    requester: string;
    id: string;
    category?: string;
}

export interface EmailOfficeWindowRequestProps {
    start_date: DateType;
    end_date: DateType;
    location: string;
    office: string;
    clinician_name?: string;
    category?: string;
    reason?: string;
}
export type INewOutboundEmail = Overwrite<
    IOutboundEmail,
    {
        id?: string;
        client_id?: string;
        clinician_id?: string;
        status?: OutboundEmailStatus;
        created_at?: Date;
        updated_at?: Date;
        retries?: number;
        sender?: EmailRecipient;
        cc?: EmailRecipient[];
        signature?: SignatureData;
        client?: IEmailClient;
        session_type?: string;
        therapist_fullname?: string;
        therapist_first_name?: string;
        appointment_details?: IEmailAppointmentDetails;
        requested_therapist?: string;
        last_error?: {};
        assignment_notes?: string;
        no_cc_internal?: boolean;
        office_request_details?: IEmailRequestDetails;
        deleted_office_window?: IEmailDeleteDetails;
        reserved_window_details?: IEmailReservedWindowDetails;
        templateId?: string;
        clinician_name?: string;
        client_email?: string;
        client_chart_link?: string;
        clinician_email?: string;
        attendee_relationship?: string;
        attendee_name?: string;
        attendee_is_minor?: boolean;
        client_name?: string;
        clinician_contact_info_client?: string;
        practice_name?: string;
        consent_form_type?: PortalSupportedFormType;
    }
>;

export interface Template {
    id: string;
    name: string;
    generation: string;
    updated_at: string;
    versions: any[];
}

interface Metadata {
    self: string;
    count: number;
}

export interface TemplateResponse {
    templates: {
        result: Template[];
    };
    _metadata: Metadata;
}

export const DEFAULT_TZ = "America/New_York";
export const DEFAULT_OFFSET = "-0500";
export const DATE_FORMAT = "MM/DD/YYYY";

export interface IMasterListSearchByOptions {
    tagKeys?: ClientTagKey[];
    email: string;
    lastname: string;
    firstname: string;
    phoneno: string;
    date_of_birth: Date | null;
}

export interface IntakeConfig {
    intakeOfficers: string[];
    intakeOfficerColors: {
        [key: string]: string;
    };
}

export interface IEmailReminder extends INewOutboundEmail {
    next_send_time?: string;
    max_reminder_count?: number;
    current_reminder_count?: number;
    is_completed?: boolean;
    last_send_time?: string;
}

export interface OfficeManagerActivities {
    timestamp: Date;
    activity: OfficeManagerActions;
    office_manager: string;
    office: string;
    start: string;
    end: string;
    location: string;
    reason?: string;
    approved_with_changes?: string;
    requested_by: string;
    assigned_to?: string;
}

export interface ConsentType {
    id: string;
    name: string;
    link: string;
}

export interface ConsentForm {
    id: string;
    name: string;
    types: ConsentType[];
}

export type ConsentFormCache = {
    [key: string]: {
        id: string;
        name: string;
        types: ConsentType[];
    };
};

export type EmailSignatureCache = {
    [key: string]: {
        id: string;
        name: string;
        pronouns: string;
        company: string;
        department: string;
        phone: string;
        directPhone: string;
        email: string;
        includeDisclaimer: boolean;
    };
};
export type SlotStat = { confirmed: number; available: number; held: number };

export type OpeningStats = {
    [key: string]: SlotStat;
};

export type LocationStats = {
    [key: string]: SlotStat & {
        slots?: number;
        hours?: number;
    };
};

export type GeneratedClientChangesNotification = {
    id: string;
    actioned_by: string;
    notification_msg: string;
    client_id: string;
};

export type RtdClientChangesNotification = GeneratedClientChangesNotification & {
    notification_datetime: number;
};

export interface ExportedAppointment {
    appointment_type: "teleconsult" | "walk_in";
    location: string;
    start: string;
    end: string;
    client_id: string;
    id: string;
    office: string;
    starting_date: string;
    recurrenceRule: RecurrenceRule;
    requested_service: string;
    fee: number;
    raw_client_email: string;
    client_email: string;
    secondary_email: string;
    client_phone_no: string;
    client_last_name: string;
    client_first_name: string;
    client_dob: string;
    client_preferred_name: string;
    is_this_week: boolean;
    next_appt_date: string;
}

export const STATES: { name: string; abbrv: string }[] = [
    { name: "AF Africa", abbrv: "AE" },
    { name: "AF American", abbrv: "AA" },
    { name: "AF Canada", abbrv: "AC" },
    { name: "AF Europe", abbrv: "AE" },
    { name: "AF Middle East", abbrv: "AE" },
    { name: "AF Pacific", abbrv: "AP" },
    { name: "Alabama", abbrv: "AL" },
    { name: "Alaska", abbrv: "AK" },
    { name: "American Samoa", abbrv: "AS" },
    { name: "Arizona", abbrv: "AZ" },
    { name: "Arkansas", abbrv: "AR" },
    { name: "California", abbrv: "CA" },
    { name: "Colorado", abbrv: "CO" },
    { name: "Connecticut", abbrv: "CT" },
    { name: "Delaware", abbrv: "DE" },
    { name: "District of Columbia", abbrv: "DC" },
    { name: "FDRS Micronesia", abbrv: "FM" },
    { name: "Florida", abbrv: "FL" },
    { name: "Georgia", abbrv: "GA" },
    { name: "Guam", abbrv: "GU" },
    { name: "Hawaii", abbrv: "HI" },
    { name: "Idaho", abbrv: "ID" },
    { name: "Illinois", abbrv: "IL" },
    { name: "Indiana", abbrv: "IN" },
    { name: "Iowa", abbrv: "IA" },
    { name: "Kansas", abbrv: "KS" },
    { name: "Kentucky", abbrv: "KY" },
    { name: "Louisiana", abbrv: "LA" },
    { name: "Maine", abbrv: "ME" },
    { name: "Marshall Islands", abbrv: "MH" },
    { name: "Maryland", abbrv: "MD" },
    { name: "Massachusetts", abbrv: "MA" },
    { name: "Michigan", abbrv: "MI" },
    { name: "Minnesota", abbrv: "MN" },
    { name: "Mississippi", abbrv: "MS" },
    { name: "Missouri", abbrv: "MO" },
    { name: "Montana", abbrv: "MT" },
    { name: "Nebraska", abbrv: "NE" },
    { name: "Nevada", abbrv: "NV" },
    { name: "New Hampshire", abbrv: "NH" },
    { name: "New Jersey", abbrv: "NJ" },
    { name: "New Mexico", abbrv: "NM" },
    { name: "New York", abbrv: "NY" },
    { name: "North Carolina", abbrv: "NC" },
    { name: "North Dakota", abbrv: "ND" },
    { name: "N. Mariana Islands", abbrv: "MP" },
    { name: "Ohio", abbrv: "OH" },
    { name: "Oklahoma", abbrv: "OK" },
    { name: "Oregon", abbrv: "OR" },
    { name: "Palau", abbrv: "PW" },
    { name: "Pennsylvania", abbrv: "PA" },
    { name: "Puerto Rico", abbrv: "PR" },
    { name: "Rhode Island", abbrv: "RI" },
    { name: "South Carolina", abbrv: "SC" },
    { name: "South Dakota", abbrv: "SD" },
    { name: "Tennessee", abbrv: "TN" },
    { name: "Texas", abbrv: "TX" },
    { name: "Utah", abbrv: "UT" },
    { name: "Vermont", abbrv: "VT" },
    { name: "Virginia", abbrv: "VA" },
    { name: "Washington", abbrv: "WA" },
    { name: "Washington, D.C.", abbrv: "DC" },
    { name: "West Virginia", abbrv: "WV" },
    { name: "Wisconsin", abbrv: "WI" },
    { name: "Wyoming", abbrv: "WY" },
];

export interface MessageStoreResponse {
    uri: string;
    records: MessageRecord[];
    paging: PagingInfo;
    navigation: NavigationInfo;
}

export interface MessageRecord {
    uri: string;
    id: number;
    to: PhoneNumber[] | string;
    from: MessageSender | string;
    type: string;
    creationTime: string;
    readStatus: string;
    priority: string;
    attachments: Attachment[];
    direction: string;
    availability: string;
    subject: string;
    messageStatus: string;
    conversationId: number;
    conversation: ConversationInfo;
    lastModifiedTime: string;
    text?: string;
    created_at?: Date;
    status?: string;
}

interface PhoneNumber {
    phoneNumber: string;
}

interface MessageSender {
    phoneNumber: string;
    location: string;
    name: string;
}

interface Attachment {
    id: number;
    uri: string;
    type: string;
    contentType: string;
}

interface ConversationInfo {
    id: string;
    uri: string;
}

interface PagingInfo {
    page: number;
    totalPages: number;
    perPage: number;
    totalElements: number;
    pageStart: number;
    pageEnd: number;
}

interface NavigationInfo {
    firstPage: NavigationUri;
    lastPage: NavigationUri;
}

interface NavigationUri {
    uri: string;
}

export interface CallStoreResponse {
    uri: string;
    records: CallLogResponse[];
    paging: PagingInfo;
    navigation: NavigationInfo;
    truncated?: boolean;
}

export interface ICommunicationSummary {
    client_id: string;
    phone_number: string;
    sms_count?: number;
    call_count?: number;
    call_count_truncated?: boolean;
}

export interface CallLogResponse {
    uri: string;
    id: string;
    sessionId: string;
    startTime: string;
    duration: number;
    type: string;
    direction: string;
    action: string;
    result: string;
    to: PhoneNumber;
    from: CallerDetails;
    transport: string;
    lastModifiedTime: string;
    legs: CallLeg[];
}

interface PhoneNumber {
    phoneNumber: string;
    name?: string;
    location?: string;
    extensionNumber?: string;
}

interface CallerDetails {
    phoneNumber: string;
    name?: string;
    location?: string;
}

interface CallLeg {
    startTime: string;
    duration: number;
    type: string;
    direction: string;
    action: string;
    result: string;
    to: PhoneNumber;
    from: CallerDetails;
    transport: string;
    legType?: string;
    extension?: ExtensionDetails;
}

interface ExtensionDetails {
    uri: string;
    id: string;
}

export interface ChartLock {
    client_id: string;
    locked_by: string;
    locked_by_name: string;
    locked_at: Date;
    last_activity: Date;
    browser_tab_id: string;
}

export interface ChartLockAttempt {
    client_id: string;
    user_email: string;
    user_name: string;
    attempted_at: Date;
    browser_tab_id: string;
}

export enum ChartLockStatus {
    AVAILABLE = "available",
    LOCKED = "locked",
    LOCKED_BY_ME = "locked_by_me",
    LOCKED_BY_OTHER = "locked_by_other",
}

export interface ChartLockInfo {
    status: ChartLockStatus;
    lock?: ChartLock;
    can_edit: boolean;
}

export interface IClientBookingToFix extends IClientBooking {
    needs_fix: boolean;
    is_archived: boolean;
    issue?: string;
}

export interface IAppointCheckResult {
    client: IClient;
    needs_fix_count: number;
    appointments: IClientBookingToFix[];
    summary: string;
    is_archived_client: boolean;
}

export type ClientRepairDiagnosticsAction = "plan" | "apply";

export type ClientRepairDiagnosticsSource = "active" | "history" | "bigquery";

export type ClientRepairDiagnosticsRepairActionType =
    | "restore_missing_client"
    | "sync_client_confirmed_to_clinician"
    | "sync_client_held_to_clinician"
    | "add_clinician_confirmed_to_client"
    | "add_clinician_held_to_client"
    | "clean_archived_booking_from_clinician";

export interface ClientRepairDiagnosticsSearchPayload {
    clientId?: string;
    clientEmail?: string;
    clinicianEmail?: string;
    actionIds?: string[];
}

export interface ClientRepairDiagnosticsRequest {
    action: ClientRepairDiagnosticsAction;
    payload: ClientRepairDiagnosticsSearchPayload;
}

export interface ClientRepairDiagnosticsClientEvidence {
    id: string;
    email?: string;
    displayName?: string;
    status?: string;
    source: ClientRepairDiagnosticsSource;
    appointmentHistoryCount?: number;
    confirmedBookingCount?: number;
    heldBookingCount?: number;
    exportedAppointmentCounts?: {
        cancelled: number;
        confirmed: number;
        held: number;
        available: number;
        total: number;
    };
}

export interface ClientRepairDiagnosticsEvidence {
    activeClients: ClientRepairDiagnosticsClientEvidence[];
    historyClients: ClientRepairDiagnosticsClientEvidence[];
    exportedClients: ClientRepairDiagnosticsClientEvidence[];
    searchedClientEmail?: string;
    searchedClientId?: string;
    searchedClinicianEmail?: string;
}

export interface ClientRepairDiagnosticsPlanAction {
    id: string;
    type: ClientRepairDiagnosticsRepairActionType;
    title: string;
    description: string;
    clientId: string;
    clinicianId?: string;
    bookingId?: string;
    source: ClientRepairDiagnosticsSource;
    safeToApply: boolean;
    blockers: string[];
    evidence: string[];
}

export interface ClientRepairDiagnosticsResponse {
    action: ClientRepairDiagnosticsAction;
    evidence: ClientRepairDiagnosticsEvidence;
    actions: ClientRepairDiagnosticsPlanAction[];
    appliedActionIds: string[];
    skippedActionIds: string[];
    blockers: string[];
}

export interface ICommunicationNoteUser {
    email: string;
    name: string;
}

export interface ICommunicationNote {
    note_id: string;
    channel: "sms" | "call";
    ringcentral_record_id: string;
    phone_number: string;
    intake_client_id: string | null;
    body: string;
    origin_system: "intake" | "mantle";
    author: ICommunicationNoteUser;
    updated_by: ICommunicationNoteUser | null;
    created_at: string;
    deleted_at: string | null;
    revision: string;
}

export type CommunicationNotesRequest =
    | { action: "list"; clientId: string; afterNoteId?: string | null }
    | {
          action: "create";
          clientId: string;
          channel: ICommunicationNote["channel"];
          recordId: string;
          body: string;
      }
    | { action: "update"; clientId: string; noteId: string; body: string }
    | { action: "delete"; clientId: string; noteId: string };
